import { useState, useMemo, useEffect } from "react";
import { Building2, LayoutDashboard, Calendar, Utensils, Target, PartyPopper, BookUser, Construction } from "lucide-react";
import { cn } from "@/lib/utils";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { TopNav } from "@/components/TopNav";
import { DashboardSidebar } from "@/components/DashboardSidebar";
import { TargetsSection } from "@/components/marketing/TargetsSection";
import { useTargets } from "@/hooks/useTargets";
import { useActivities } from "@/hooks/useActivities";
import { WeeklyCalendar } from "@/components/marketing/WeeklyCalendar";
import { AddActivityDialog } from "@/components/marketing/AddActivityDialog";
import { ActivityDetailDialog } from "@/components/marketing/ActivityDetailDialog";
import { OverdueActivitiesAlert } from "@/components/marketing/OverdueActivitiesAlert";
import { EventsModule } from "@/components/events/EventsModule";
import { useEvents } from "@/hooks/useEvents";
import { CateringModule } from "@/components/catering/CateringModule";
import { VendorDirectory } from "@/components/vendors/VendorDirectory";
import { DashboardHome } from "@/components/dashboard/DashboardHome";

const MODULES = {
  dashboard: { title: "Dashboard", sub: "Track your local marketing outreach", icon: LayoutDashboard,
    desc: "Community feed, national marketing highlights, and weekly outreach progress for your store." },
  calendar: { title: "Marketing Calendar", sub: "View and manage your marketing schedule", icon: Calendar,
    desc: "Weekly calendar of activities, events, holidays, and national marketing dates." },
  contacts: { title: "Targets", sub: "View and manage all your targets", icon: Target,
    desc: "Target categories, subtypes, and leads with outreach scheduling." },
  events: { title: "Events", sub: "Manage your event outreach", icon: PartyPopper,
    desc: "Event planning, contacts, and attachments for local store marketing events." },
  catering: { title: "Catering", sub: "Manage your catering clients and orders", icon: Utensils,
    desc: "Catering clients, orders, billing, and activity history." },
  vendors: { title: "Vendor Directory", sub: "Local vendors and partners", icon: BookUser,
    desc: "Directory of vendors and partners available to your store." },
};

function StoreTabs({ stores, selected, onSelect }) {
  if (stores.length <= 1) return null;
  return (
    <div className="mb-4 border-b border-border">
      <div className="flex gap-1 overflow-x-auto pb-px">
        {stores.map((store) => (
          <button key={store} data-testid={`store-tab-${store}`} onClick={() => onSelect(store)}
            className={cn(
              "flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-t-lg transition-colors whitespace-nowrap",
              selected === store ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground hover:bg-muted"
            )}>
            <Building2 className="h-4 w-4" /> Store {store}
          </button>
        ))}
      </div>
    </div>
  );
}

function ModulePlaceholder({ module }) {
  const Icon = module.icon;
  return (
    <div className="space-y-6 animate-fade-in" data-testid="module-content">
      <div>
        <h2 className="text-2xl font-bold text-foreground">{module.title}</h2>
        <p className="text-muted-foreground">{module.sub}</p>
      </div>
      <div className="rounded-lg border bg-card card-shadow p-12 flex flex-col items-center text-center">
        <div className="h-14 w-14 rounded-full bg-primary/10 flex items-center justify-center mb-4">
          <Icon className="h-7 w-7 text-primary" />
        </div>
        <h3 className="text-lg font-semibold text-foreground">{module.title} module</h3>
        <p className="text-muted-foreground max-w-md mt-2 text-sm">{module.desc}</p>
        <div className="mt-5 inline-flex items-center gap-2 text-xs font-medium text-amber-700 bg-amber-50 border border-amber-200 px-3 py-1.5 rounded-full">
          <Construction className="h-3.5 w-3.5" /> Being migrated from your original app — coming in the next phase
        </div>
      </div>
    </div>
  );
}

function TargetsTab({ storeCode }) {
  const targetsApi = useTargets(storeCode);
  if (targetsApi.loading) {
    return <div className="flex items-center justify-center py-20"><div className="h-8 w-8 border-2 border-muted border-t-primary rounded-full animate-spin" /></div>;
  }
  return (
    <div className="space-y-6 animate-fade-in" data-testid="module-content">
      <div>
        <h2 className="text-2xl font-bold text-foreground">Targets</h2>
        <p className="text-muted-foreground">View and manage all your targets{storeCode ? ` for store ${storeCode}` : ""}</p>
      </div>
      <TargetsSection {...targetsApi} />
    </div>
  );
}

function CalendarTab({ storeCode }) {
  const acts = useActivities(storeCode);
  const [leads, setLeads] = useState([]);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [addOpen, setAddOpen] = useState(false);
  const [addDate, setAddDate] = useState(new Date());
  const [detail, setDetail] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);

  useEffect(() => {
    api.get("/leads", { params: storeCode ? { store_code: storeCode } : {} })
      .then(({ data }) => setLeads(data)).catch(() => {});
  }, [storeCode]);

  const openAdd = (date) => { setAddDate(date || new Date()); setAddOpen(true); };
  const openDetail = (a) => { setDetail(a); setDetailOpen(true); };

  if (acts.loading) {
    return <div className="flex items-center justify-center py-20"><div className="h-8 w-8 border-2 border-muted border-t-primary rounded-full animate-spin" /></div>;
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="module-content">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-foreground">Marketing Calendar</h2>
          <p className="text-muted-foreground">Scheduled & completed outreach{storeCode ? ` for store ${storeCode}` : ""}</p>
        </div>
        <button data-testid="calendar-add-activity" onClick={() => openAdd(new Date())}
          className="inline-flex items-center gap-2 bg-primary text-primary-foreground px-4 py-2 rounded-lg text-sm font-medium hover:bg-primary/90 transition-colors">
          + Log / Schedule
        </button>
      </div>
      <OverdueActivitiesAlert activities={acts.activities} onActivityClick={openDetail} />
      <WeeklyCalendar
        selectedDate={selectedDate} onSelectDate={setSelectedDate}
        activities={acts.activities} onAddActivity={openAdd}
        onUpdateActivity={acts.updateActivity} onActivityClick={openDetail} />
      <AddActivityDialog open={addOpen} onOpenChange={setAddOpen} onAdd={acts.addActivity} defaultDate={addDate} leads={leads} />
      <ActivityDetailDialog activity={detail} open={detailOpen} onOpenChange={setDetailOpen}
        onUpdate={acts.updateActivity} onDelete={acts.deleteActivity} onComplete={acts.completeActivity} />
    </div>
  );
}

function EventsTab({ storeCode }) {
  const ev = useEvents(storeCode);
  const [leads, setLeads] = useState([]);
  useEffect(() => {
    api.get("/leads", { params: storeCode ? { store_code: storeCode } : {} })
      .then(({ data }) => setLeads(data)).catch(() => {});
  }, [storeCode]);

  if (ev.loading) {
    return <div className="flex items-center justify-center py-20"><div className="h-8 w-8 border-2 border-muted border-t-primary rounded-full animate-spin" /></div>;
  }
  return (
    <div className="space-y-6 animate-fade-in" data-testid="module-content">
      <div>
        <h2 className="text-2xl font-bold text-foreground">Events</h2>
        <p className="text-muted-foreground">Manage your event outreach{storeCode ? ` for store ${storeCode}` : ""}</p>
      </div>
      <EventsModule events={ev.events} onAddEvent={ev.addEvent} onUpdateEvent={ev.updateEvent} onDeleteEvent={ev.deleteEvent} leads={leads} />
    </div>
  );
}

function CateringTab({ storeCode }) {
  const activitiesApi = useActivities(storeCode);
  const targetsApi = useTargets(storeCode);
  if (activitiesApi.loading || targetsApi.loading) {
    return <div className="flex items-center justify-center py-20"><div className="h-8 w-8 border-2 border-muted border-t-primary rounded-full animate-spin" /></div>;
  }
  return (
    <div className="space-y-6 animate-fade-in" data-testid="module-content">
      <div>
        <h2 className="text-2xl font-bold text-foreground">Catering</h2>
        <p className="text-muted-foreground">Manage your catering clients and orders{storeCode ? ` for store ${storeCode}` : ""}</p>
      </div>
      <CateringModule activitiesApi={activitiesApi} targetsApi={targetsApi} storeCode={storeCode} />
    </div>
  );
}

export default function Dashboard() {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState("dashboard");
  const [fetchedStores, setFetchedStores] = useState([]);

  const ownStores = useMemo(() => {
    const stores = [];
    if (user?.store_code) stores.push(user.store_code);
    (user?.accessible_stores || []).forEach((s) => { if (!stores.includes(s)) stores.push(s); });
    return stores;
  }, [user?.store_code, user?.accessible_stores]);

  // Franchise/super admins may have no explicit store assignment — pull their franchise stores.
  useEffect(() => {
    const role = user?.role;
    const isAdmin = role === "super_admin" || role === "admin" || role === "franchisee_admin";
    if (isAdmin && ownStores.length === 0) {
      api.get("/stores").then(({ data }) => setFetchedStores(data.map((s) => s.store_code))).catch(() => {});
    }
  }, [user?.role, ownStores.length]);

  const allStores = ownStores.length ? ownStores : fetchedStores;
  const [selectedStore, setSelectedStore] = useState("");
  useEffect(() => {
    if (!selectedStore && allStores.length) setSelectedStore(allStores[0]);
  }, [allStores, selectedStore]);

  return (
    <div className="min-h-screen bg-background">
      <TopNav />
      <div className="flex">
        <div className="w-[20%] min-w-[200px] max-w-[280px] h-[calc(100vh-64px)] sticky top-[64px]">
          <DashboardSidebar activeTab={activeTab} onTabChange={setActiveTab} />
        </div>
        <main className="flex-1 px-6 py-6">
          <StoreTabs stores={allStores} selected={selectedStore} onSelect={setSelectedStore} />
          {activeTab === "dashboard"
            ? <DashboardHome key={selectedStore} storeCode={selectedStore} onNavigate={setActiveTab} />
            : activeTab === "contacts"
            ? <TargetsTab key={selectedStore} storeCode={selectedStore} />
            : activeTab === "calendar"
            ? <CalendarTab key={selectedStore} storeCode={selectedStore} />
            : activeTab === "events"
            ? <EventsTab key={selectedStore} storeCode={selectedStore} />
            : activeTab === "catering"
            ? <CateringTab key={selectedStore} storeCode={selectedStore} />
            : activeTab === "vendors"
            ? <VendorDirectory />
            : <ModulePlaceholder module={MODULES[activeTab]} />}
        </main>
      </div>
    </div>
  );
}
